import { Dialog, VisuallyHidden } from 'radix-ui';
import { type FC, type ReactNode } from 'react';

import { SECTIONS_IDS } from '../../../../../constants/sections';
import { useLkBasePath } from '../../../../../hooks/use-lk-base-path';
import { useMainLandingSectionHrefFactory } from '../../../../../hooks/use-main-landing-href';
import { ChevronForwardV4Icon } from '../../../../icons/ChevronForwardV4Icon';
import { MenuCloseV4Icon } from '../../../../icons/MenuCloseV4Icon';
import { ProfileV4Icon } from '../../../../icons/ProfileV4Icon';
import { ButtonLink } from '../../../common/ButtonLink';
import { Flex } from '../../../common/Flex';
import { Link } from '../../../common/Link';
import { Logo } from '../../../common/Logo';
import { LogoText } from '../../../common/LogoText';
import { Text } from '../../../common/Text';
import { TextLink } from '../../../common/TextLink';

import styles from './MobileModalMenu.module.scss';

type MobileModalMenuProps = {
  children: ReactNode;
};

const SECTION_LINKS: { sectionId: string; title: string }[] = [
  {
    sectionId: SECTIONS_IDS.products,
    title: 'What we do',
  },
  {
    sectionId: SECTIONS_IDS.solutions,
    title: 'Solutions',
  },
  {
    sectionId: SECTIONS_IDS.developers,
    title: 'Developers',
  },
  {
    sectionId: SECTIONS_IDS.resources,
    title: 'Resources',
  },
];

export const MobileModalMenu: FC<MobileModalMenuProps> = (props) => {
  const { children } = props;

  const lkBasePath = useLkBasePath();
  const sectionHref = useMainLandingSectionHrefFactory();

  return (
    <Dialog.Root defaultOpen={false}>
      <Dialog.Trigger asChild>{children}</Dialog.Trigger>

      <Dialog.Portal>
        <VisuallyHidden.Root>
          <Dialog.Title>Mobile menu</Dialog.Title>

          <Dialog.Description>Mobile menu</Dialog.Description>
        </VisuallyHidden.Root>

        <Dialog.Content className={styles.content}>
          <Flex className={styles.container} direction="column" gap={10}>
            <Flex alignItems="center" justifyContent="spaceBetween">
              <Dialog.Close asChild>
                <Link className={styles.link} href={sectionHref(SECTIONS_IDS.first)}>
                  <Flex className={styles.logoContainer} gap={3} alignItems="center" justifyContent="center">
                    <LogoText />

                    <Logo />
                  </Flex>
                </Link>
              </Dialog.Close>

              <Dialog.Close asChild>
                <Flex className={styles.closeButton} alignItems="center" justifyContent="center">
                  <MenuCloseV4Icon />
                </Flex>
              </Dialog.Close>
            </Flex>

            <Flex className={styles.menu} direction="column" gap={4} justifyContent="spaceBetween">
              <Flex direction="column" gap={16}>
                <Flex direction="column" gap={8}>
                  {SECTION_LINKS.map(({ sectionId, title }, index) => (
                    <Dialog.Close key={index} asChild>
                      <Link className={styles.link} href={sectionHref(sectionId)}>
                        <Text type="regular16">{title}</Text>

                        <ChevronForwardV4Icon />
                      </Link>
                    </Dialog.Close>
                  ))}

                  <Dialog.Close asChild>
                    <Link className={styles.link} href="/why-sg">
                      <Text type="regular16">Why SmartGates</Text>

                      <ChevronForwardV4Icon />
                    </Link>
                  </Dialog.Close>

                  <Dialog.Close asChild>
                    <Link className={styles.link} href="/integrations">
                      <Text type="regular16">Integrations</Text>

                      <ChevronForwardV4Icon />
                    </Link>
                  </Dialog.Close>
                </Flex>

                <Flex gap={4} alignItems="center">
                  <ProfileV4Icon />

                  <TextLink href={`${lkBasePath}/account/login`} isOutsideLink>
                    <Flex gap={2} alignItems="center">
                      <span>Log in</span>

                      <ChevronForwardV4Icon />
                    </Flex>
                  </TextLink>
                </Flex>
              </Flex>

              <Dialog.Close asChild>
                <ButtonLink href={sectionHref(SECTIONS_IDS.contact)}>Contact</ButtonLink>
              </Dialog.Close>
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
